<template>
  <div class="message-templates-page relative flex h-full w-full flex-col">
    <message-templates-header
      has-search
      :header-title="headerTitle"
      :search-query="searchQuery"
      :message-template-type="messageTemplateType"
      @change-search-query="changeSearchQuery"
      @create-template="redirectToBuilder"
    />

    <div class="relative flex flex-grow overflow-hidden">
      <message-templates-table
        :message-templates="templates"
        :sort-config="sortConfig"
        :class="wrapClass"
        :message-template-type="messageTemplateType"
        @hide-info-panel="hideInfoPanel"
        @select-message-template="changeSelectedMessageTemplate"
        @on-sort-change="onSortChange"
        @change-selected-translation-language="
          changeSelectedTranslationLanguage
        "
      />

      <message-template-info-panel
        v-if="showInfoPanel"
        :selected-translation-language="selectedTranslationLanguage"
        :message-template-type="messageTemplateType"
        @close="hideInfoPanel"
        @delete-template="onDeleteTemplate"
        @fetch-message-templates="fetchMessageTemplates"
        @fetch-message-template="fetchMessageTemplate"
        @change-selected-translation-language="
          changeSelectedTranslationLanguage
        "
      />
    </div>

    <table-footer
      class="mt-auto"
      :current-page="currentPage"
      :total-count="records.totalCount"
      :page-size="pageSize"
      @page-change="onPageChange"
    />
  </div>
</template>

<script>
// Libs //
import { mapGetters } from 'vuex';

// Mixins //
import alertMixin from 'shared/mixins/alertMixin';
import { hasWhatsAppInbox } from 'shared/mixins/inboxMixin';

// Components //
import MessageTemplatesTable from '../components/MessageTemplatesTable.vue';
import MessageTemplatesHeader from '../components/MessageTemplatesHeader.vue';
import MessageTemplateInfoPanel from '../components/MessageTemplateInfoPanel.vue';
import TableFooter from '../../../../components/widgets/TableFooter.vue';

// Constants //
import {
  MESSAGE_TEMPLATES_CHANNEL_TYPE,
  TEMPLATE_BUILDER_MODE,
} from '../constants';
import templateTypes from '../../../../store/mutation-types';

const DEFAULT_PAGE = 1;

const typeSearchQueryTimeout = {
  [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: null,
  [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: null,
};

export default {
  name: 'MessageTemplates',

  components: {
    MessageTemplatesTable,
    MessageTemplatesHeader,
    MessageTemplateInfoPanel,
    TableFooter,
  },

  mixins: [alertMixin],

  props: {
    messageTemplateType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      DEFAULT_PAGE,
      MESSAGE_TEMPLATES_CHANNEL_TYPE,
      pageSize: 20,
      typePage: {
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: DEFAULT_PAGE,
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: DEFAULT_PAGE,
      },
      typeSearchQuery: {
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: '',
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: '',
      },
      typeSortConfig: {
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: { createdAt: 'desc' },
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: { createdAt: 'desc' },
      },
      typeSelectedTranslationLanguage: {
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: '',
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: '',
      },
      typeSelectedMessageTemplateId: {
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: '',
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: '',
      },
      typeShowInfoPanel: {
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP]: false,
        [MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS]: false,
      },
    };
  },

  computed: {
    ...mapGetters({
      records: 'messageTemplates/getMessageTemplates',
      record: 'messageTemplates/getMessageTemplate',
      currentAccountId: 'getCurrentAccountId',
      inboxes: 'inboxes/getInboxes',
      isSuperAdmin: 'getIsSuperAdmin',
    }),

    wrapClass() {
      return this.showInfoPanel ? 'me-[var(--contact-panel-width)]' : '';
    },

    headerTitle() {
      return this.$t(
        `CHATLYN_MESSAGE_TEMPLATE.TITLE.${this.messageTemplateType.toUpperCase()}`
      );
    },

    query() {
      return this.$route.query;
    },

    queryPage: {
      get() {
        return Number(this.query?.page);
      },

      set(page) {
        this.$router.replace({ path: this.$route.path, query: { page } });
      },
    },

    currentPage: {
      get() {
        const selectedPageNumber =
          this.queryPage ||
          this.typePage[this.messageTemplateType] ||
          DEFAULT_PAGE;

        return selectedPageNumber;
      },

      set(page) {
        this.typePage = structuredClone({
          ...this.typePage,
          [this.messageTemplateType]: page,
        });

        this.queryPage = page;
      },
    },

    searchQuery: {
      get() {
        return this.typeSearchQuery[this.messageTemplateType];
      },

      set(newValue) {
        this.typeSearchQuery = structuredClone({
          ...this.typeSearchQuery,
          [this.messageTemplateType]: newValue,
        });
      },
    },

    sortConfig: {
      get() {
        return this.typeSortConfig[this.messageTemplateType];
      },

      set(newValue) {
        this.typeSortConfig = structuredClone({
          ...this.typeSortConfig,
          [this.messageTemplateType]: newValue,
        });
      },
    },

    templates() {
      switch (this.messageTemplateType) {
        case MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS:
          return this.records.smsTemplates;

        case MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP:
          return this.records.templates;

        default:
          return [];
      }
    },

    selectedTranslationLanguage: {
      get() {
        return this.typeSelectedTranslationLanguage[this.messageTemplateType];
      },

      set(newValue) {
        this.typeSelectedTranslationLanguage = structuredClone({
          ...this.typeSelectedTranslationLanguage,
          [this.messageTemplateType]: newValue,
        });
      },
    },

    showInfoPanel: {
      get() {
        return this.typeShowInfoPanel[this.messageTemplateType];
      },

      set(newValue) {
        this.typeShowInfoPanel = structuredClone({
          ...this.typeShowInfoPanel,
          [this.messageTemplateType]: newValue,
        });
      },
    },

    selectedMessageTemplateId: {
      get() {
        return this.typeSelectedMessageTemplateId[this.messageTemplateType];
      },

      set(newValue) {
        this.typeSelectedMessageTemplateId = structuredClone({
          ...this.typeSelectedMessageTemplateId,
          [this.messageTemplateType]: newValue,
        });
      },
    },
  },

  watch: {
    messageTemplateType: {
      async handler() {
        await this.onTemplateTypeChange();
      },
      immediate: true,
    },

    $route: {
      handler(newValue, prevValue) {
        if (
          !newValue?.name ||
          !prevValue?.name ||
          newValue.name !== prevValue.name ||
          !!newValue.query.page
        ) {
          return;
        }

        this.queryPage = this.currentPage;
      },
      immediate: true,
    },
  },

  async mounted() {
    // Mirror the sidebar rule at page level — without a WhatsApp inbox, the
    // WhatsApp templates page is superadmin-only (drafts can be created
    // before onboarding); direct URL access by admins is blocked.
    if (
      this.messageTemplateType === MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP &&
      !this.isSuperAdmin
    ) {
      await this.$store.dispatch('inboxes/get');
      if (!hasWhatsAppInbox(this.inboxes)) {
        this.$router.replace({ name: 'home' });
      }
    }
  },

  methods: {
    async onTemplateTypeChange() {
      if (!Number(this.query?.page)) {
        this.currentPage =
          this.typePage[this.messageTemplateType] || DEFAULT_PAGE;
      }

      this.$store.commit(
        `messageTemplates/${templateTypes.CLEAR_DELETING_MESSAGE_TEMPLATES}`
      );

      await this.fetchMessageTemplates();

      if (this.showInfoPanel) {
        await this.fetchMessageTemplate(this.selectedMessageTemplateId);
      }
    },

    hideInfoPanel() {
      this.showInfoPanel = false;
      this.$store.commit(
        `messageTemplates/${templateTypes.CLEAR_MESSAGE_TEMPLATE}`
      );
      this.selectedMessageTemplateId = '';
      this.changeSelectedTranslationLanguage('');
    },

    async onDeleteTemplate() {
      const { _id: id, title } = this.record;
      this.hideInfoPanel();

      const status = await this.deleteTemplate(id);

      if (!status) {
        this.showAlert(
          this.$t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.ERROR_MESSAGE', {
            TEMPLATE_NAME: title,
          })
        );
        return;
      }

      this.showAlert(
        this.$t('CHATLYN_MESSAGE_TEMPLATE.DELETE_MODAL.SUCCESS_MESSAGE', {
          TEMPLATE_NAME: title,
        })
      );

      const shouldGoToPrevPage =
        this.templates.length < 2 && this.currentPage > 1;

      if (shouldGoToPrevPage) {
        this.currentPage -= 1;
      }

      await this.fetchMessageTemplates(this.currentPage);
    },

    deleteTemplate(templateId) {
      switch (this.messageTemplateType) {
        case MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS:
          return this.$store.dispatch(
            'messageTemplates/deleteSmsMessageTemplate',
            {
              templateId,
              accountId: this.currentAccountId,
            }
          );

        case MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP:
          return this.$store.dispatch(
            'messageTemplates/deleteMessageTemplate',
            {
              templateId,
              accountId: this.currentAccountId,
            }
          );

        default:
          return Promise.resolve(false);
      }
    },

    handleShowInfoPanel() {
      this.showInfoPanel = true;
    },

    async changeSelectedMessageTemplate(messageTemplateId) {
      this.selectedMessageTemplateId = messageTemplateId;
      await this.fetchMessageTemplate(messageTemplateId);

      // Only set language if not already selected (e.g., from clicking a language badge)
      // or if the currently selected language doesn't exist in the fetched template
      const hasValidSelectedLanguage =
        this.selectedTranslationLanguage &&
        this.record?.translations?.some(
          (t) => t.language === this.selectedTranslationLanguage
        );

      if (!hasValidSelectedLanguage && this.record?.translations?.[0]?.language) {
        this.selectedTranslationLanguage = this.record.translations[0].language;
      }

      this.handleShowInfoPanel();
    },

    async fetchMessageTemplates(page) {
      const requestParams = {
        page: page || this.currentPage,
        sortAttr: this.getSortAttribute(),
        search: this.searchQuery,
        limit: this.pageSize,
        // label: this.label,
      };

      switch (this.messageTemplateType) {
        case MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS:
          await this.$store.dispatch('management/getSms', {
            accountId: this.currentAccountId,
          });
          await this.$store.dispatch(
            'messageTemplates/getSmsMessageTemplates',
            {
              accountId: this.currentAccountId,
              requestParams,
            }
          );
          break;

        case MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP:
          await this.$store.dispatch('messageTemplates/getMessageTemplates', {
            accountId: this.currentAccountId,
            requestParams,
          });
          break;

        default:
          break;
      }
    },

    async fetchMessageTemplate(messageTemplateId) {
      if (!messageTemplateId) {
        return;
      }

      switch (this.messageTemplateType) {
        case MESSAGE_TEMPLATES_CHANNEL_TYPE.SMS:
          await this.$store.dispatch('messageTemplates/getSmsMessageTemplate', {
            templateId: messageTemplateId,
          });
          break;

        case MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP:
          await this.$store.dispatch('messageTemplates/getMessageTemplate', {
            templateId: messageTemplateId,
          });
          break;

        default:
          break;
      }
    },

    onSortChange(params) {
      this.sortConfig = params;
      this.fetchMessageTemplates();
    },

    getSortAttribute() {
      let sortAttr = Object.keys(this.sortConfig).reduce((acc, sortKey) => {
        const sortOrder = this.sortConfig[sortKey];

        if (sortOrder) {
          const sortOrderSign = sortOrder === 'asc' ? '' : '-';
          return `${sortOrderSign}${sortKey}`;
        }

        return acc;
      }, '');

      if (!sortAttr) {
        this.sortConfig = { createdAt: 'desc' };
        sortAttr = '-createdAt';
      }

      return sortAttr;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.hideInfoPanel();
      this.fetchMessageTemplates(page);
    },
    redirectToBuilder() {
      this.$router.push({
        name: 'template_builder',
        params: {
          type: this.messageTemplateType,
          mode: TEMPLATE_BUILDER_MODE.CREATE,
        },
      });
    },

    changeSearchQuery(newValue) {
      this.searchQuery = newValue;

      clearInterval(typeSearchQueryTimeout[this.messageTemplateType]);

      typeSearchQueryTimeout[this.messageTemplateType] = setTimeout(
        async () => {
          if (this.currentPage !== 1) {
            this.currentPage = 1;
          }

          await this.fetchMessageTemplates();
        },
        300
      );
    },

    changeSelectedTranslationLanguage(newValue) {
      this.selectedTranslationLanguage = newValue;
    },
  },
};
</script>

<style lang="scss">
.message-templates-page {
  width: 100%;

  .left-wrap {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}
</style>
